/**
 * RegionLandingPage — /regions/:slug
 *
 * Renders a SEO-optimized, country/region-specific landing page for
 * FlavorFeed AI. Ranks for "restaurant POS {country}" + "restaurant POS
 * {city}" long-tail queries. Each page ~1,000 words with FAQ schema +
 * LocalBusiness + hreflang.
 */
import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, MapPin, Shield, Zap, Globe, ArrowRight } from 'lucide-react';
import { REGIONS } from './data';

const RegionLandingPage = () => {
  const { slug } = useParams();
  const data = REGIONS[slug?.toLowerCase()];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  const canonical = `https://www.flavorfeed.ai/regions/${data.slug}`;
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const localBiz = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `FlavorFeed AI — Restaurant POS in ${data.country}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    offers: { '@type': 'Offer', price: '0', priceCurrency: data.currency === 'Multi' ? 'USD' : data.currency },
    areaServed: { '@type': 'Country', name: data.country },
    inLanguage: data.languages,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <meta name="keywords" content={`restaurant POS ${data.country}, cloud POS ${data.country}, ${data.cities.slice(0, 5).map(c => `POS ${c}`).join(', ')}, FlavorFeed ${data.countryShort}`} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang={data.hreflang} href={canonical} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBiz)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_80%_at_50%_0%,rgba(139,92,246,0.2),transparent)]" />
        <div className="mx-auto max-w-6xl">
          <nav className="mb-6 flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/regions/united-states" className="hover:text-white">Regions</Link>
            <span>/</span>
            <span className="text-white">{data.country}</span>
          </nav>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-200">
                <span className="text-lg">{data.flag}</span>
                Available in {data.country}
              </div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl leading-tight">
                {data.heroTitle}
              </h1>
              <p className="mt-6 text-lg text-slate-300 sm:text-xl">{data.subtitle}</p>
              <ul className="mt-8 space-y-2.5">
                {data.heroBullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-base text-slate-200">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-400" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/signup?plan=free"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-3 text-base font-semibold shadow-lg shadow-violet-900/50 transition hover:from-violet-500 hover:to-fuchsia-500"
                  data-testid={`region-${data.slug}-hero-cta`}
                >
                  Start Free in {data.country} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 px-6 py-3 text-base font-semibold text-slate-100 transition hover:border-slate-500"
                >
                  Book 10-min demo
                </Link>
              </div>
              <p className="mt-3 text-[12px] text-slate-500">
                Available in {data.languages.join(' · ')} · {data.currency === 'Multi' ? 'Multi-currency' : data.currency}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur">
              <h3 className="text-sm font-semibold text-slate-400">Quick facts · {data.country}</h3>
              <div className="mt-4 space-y-4">
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-violet-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-400">Languages</div>
                    <div className="text-sm font-semibold text-white">{data.languages.join(' / ')}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-violet-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-400">Currency</div>
                    <div className="text-sm font-semibold text-white">{data.currency}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-violet-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-400">Compliance</div>
                    <div className="text-sm font-semibold text-white">Fully tax-compliant</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-violet-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-400">Onboarding</div>
                    <div className="text-sm font-semibold text-white">Live in under 10 minutes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-bold">Restaurants using FlavorFeed across {data.country}</h2>
          <p className="mt-2 text-slate-400">From single-location cafés to multi-branch chains — FlavorFeed scales across major cities.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {data.cities.map((c) => (
              <span key={c} className="rounded-full border border-slate-700 bg-slate-900/60 px-3.5 py-1.5 text-sm text-slate-200">
                <MapPin className="mr-1 inline h-3 w-3" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Regulations / Compliance */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold">Built for {data.country} compliance</h2>
          <p className="mt-2 text-slate-400">Tax, invoicing, and regulatory requirements handled out of the box.</p>
          <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
            <ul className="space-y-3">
              {data.regulations.map((r) => (
                <li key={r} className="flex items-start gap-3 text-base text-slate-200">
                  <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-400" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold">Common questions from {data.country}</h2>
          <div className="mt-6 space-y-3">
            {data.faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <summary className="cursor-pointer list-none font-semibold text-white">
                  <div className="flex items-start justify-between gap-4">
                    <span>{f.q}</span>
                    <span className="transition group-open:rotate-45 text-2xl leading-none text-slate-500">+</span>
                  </div>
                </summary>
                <p className="mt-3 text-slate-300 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-3xl rounded-3xl border border-violet-500/40 bg-gradient-to-br from-violet-600/20 via-fuchsia-600/10 to-transparent p-10 text-center">
          <h2 className="text-3xl font-bold">Ready to modernize your {data.country} restaurant?</h2>
          <p className="mt-3 text-slate-300">Free forever tier. No contract. Works with hardware you already own.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/signup?plan=free"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3.5 text-base font-semibold shadow-lg shadow-violet-900/50 transition hover:from-violet-500 hover:to-fuchsia-500"
              data-testid={`region-${data.slug}-final-cta`}
            >
              Start Free Now
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-800/50 px-7 py-3.5 text-base font-semibold text-slate-100 transition hover:border-slate-500"
            >
              Home
            </Link>
          </div>
        </div>
      </section>

      {/* Other regions */}
      <section className="border-t border-slate-800 px-6 py-10">
        <div className="mx-auto max-w-5xl">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Also available in</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.values(REGIONS)
              .filter((r) => r.slug !== data.slug)
              .map((r) => (
                <Link
                  key={r.slug}
                  to={`/regions/${r.slug}`}
                  className="rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm text-slate-200 transition hover:border-violet-500 hover:text-white"
                >
                  {r.flag} {r.country} →
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegionLandingPage;
